import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid
} from '@mui/material';
import { Box } from '@mui/system';
import { Document, pdfjs, Page as Pages } from 'react-pdf';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { BiCloudUpload } from 'react-icons/bi';
import Loader from '../../../Global/Loader';
import useAgreementSelector from '../../../../store/Slice/Agreement/AgreementSelector';
import { setApiError } from '../../../../store/Slice/Error/ErrorSlice';
import useLoginSelector from '../../../../store/Slice/Login/LoginSelector';
import {
  getAgreementDocTypes,
  uploadAgreementDoc
} from '../../../../store/Slice/Agreement/AgreementSlice';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function AggrementAddStep4({
  handlePrev,
  setActiveStep,
  reviewData,
  setReviewData
}) {
  const [documentsState, setDocumentsState] = useState({}); // { [typeId]: { file, preview, contentType } }
  const [pageNumber] = useState(1);
  const [agreementId, setAgreementId] = useState('');
  const [open3, setOpen3] = useState(false);
  const [validationMsg3, setValidationMsg3] = useState('');
  const [openLoader, setOpenLoader] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();
  const { AgreementEdit, AgreementDocTypesDrop } = useAgreementSelector();
  const { LoginData } = useLoginSelector();

  const handleClose3 = () => {
    setOpen3(false);
  };

  const handleFileUpload = (e, docType) => {
    const file = e.target.files[0];
    if (file) {
      const extension = file.name.split('.').pop();
      setDocumentsState((prev) => ({
        ...prev,
        [docType.value]: {
          file,
          preview: URL.createObjectURL(file),
          contentType: extension === 'pdf' ? 'application/pdf' : file.type,
          extension: extension
        }
      }));
    }
  };

  const onDocumentLoadSuccess = React.useCallback(({ numPages }) => {
    // optional logic
  }, []);

  const handleNextStep4 = async () => {
    // 1️⃣ Check mandatory documents
    const missingDocs = AgreementDocTypesDrop.filter((dt) => !documentsState[dt.value]?.file);

    if (missingDocs.length > 0) {
      setOpen3(true);
      setValidationMsg3(
        `Please upload all mandatory documents: ${missingDocs.map((d) => d.label).join(', ')}`
      );
      return;
    }

    // 2️⃣ Check file sizes (<10MB)
    const oversizedDocs = Object.values(documentsState).filter(
      (doc) => doc?.file && doc.file.size > 10000000
    );

    if (oversizedDocs.length > 0) {
      setOpen3(true);
      setValidationMsg3('Each uploaded file should be less than 10MB');
      return;
    }

    setOpenLoader(true);

    try {
      // 3️⃣ Upload all documents in parallel (no await inside loop)
      const uploadPromises = AgreementDocTypesDrop.map(async (docType) => {
        const doc = documentsState[docType.value];

        if (doc?.file) {
          const formUploadData = new FormData();
          formUploadData.append('agreementId', agreementId);
          formUploadData.append('companyId', LoginData?.companyId);
          formUploadData.append('userId', LoginData?.userId);
          formUploadData.append('documentTypeId', docType.value);
          formUploadData.append('documentName', docType.label);
          formUploadData.append('documentFile', doc.file);

          const result = await dispatch(uploadAgreementDoc(formUploadData, LoginData?.jwt));
          const newReviewData = {
            ...reviewData,
            documentsInfo: formUploadData
          };
          localStorage.setItem('reviewData', JSON.stringify(newReviewData));

          if (!result) {
            throw new Error(`Failed to upload ${docType.label}`);
          }

          return {
            ...result,
            documentName: docType.label,
            documentTypeId: docType.value,
            fileName: doc.file?.name
          };
        }

        return null;
      });

      const uploadedResults = (await Promise.all(uploadPromises)).filter(Boolean);

      // 4️⃣ Safety check
      if (uploadedResults.length === 0) {
        throw new Error('No documents were uploaded');
      }

      // 5️⃣ Move to next step
      setActiveStep((prev) => prev + 1);

      const lastDoc = uploadedResults[uploadedResults.length - 1];

      const updatedReviewData = {
        ...reviewData,
        documents: uploadedResults,
        contentType: lastDoc?.contentType,
        documentUrl: lastDoc?.documentUrl
      };

      setReviewData(updatedReviewData);
      localStorage.setItem('reviewData', JSON.stringify(updatedReviewData));

      setOpenLoader(false);
    } catch (error) {
      setOpenLoader(false);
      dispatch(setApiError(error?.message || 'Something went wrong during upload'));
    }
  };

  useEffect(() => {
    dispatch(getAgreementDocTypes());

    const localAgreement = JSON.parse(localStorage.getItem('agreementId'));
    if (localAgreement !== null) {
      setAgreementId(localAgreement.agreementId || localAgreement.data);
    }

    const authReviewData = JSON.parse(localStorage.getItem('reviewData'));
    if (authReviewData?.documents) {
      // Map existing documents back to state
      const existingDocs = {};
      authReviewData.documents.forEach((doc) => {
        // We might not have the file object for existing docs, just preview
        existingDocs[doc.documentTypeId || doc.typeId] = {
          preview: doc.documentUrl,
          contentType: doc.contentType,
          extension: doc.contentType === 'application/pdf' ? 'pdf' : 'img'
        };
      });
      // setDocumentsState(existingDocs); // Re-enabling this might need careful handling if editing
    }
  }, []);

  return (
    <Box>
      <Typography variant="body1" className="headingLineUp headingLineUpOut">
        Upload Documents
      </Typography>
      <Box className="viewLeadCard cardTopMinusMargin" mb={3} p={2}>
        <Grid container spacing={3}>
          {AgreementDocTypesDrop.map((docType) => {
            const doc = documentsState[docType.value];
            const isPdf = doc?.extension === 'pdf' || doc?.contentType === 'application/pdf';

            return (
              <Grid item xs={12} sm={6} md={3} key={docType.value}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    {docType.label}
                  </Typography>
                  <Box className="uploadBox" sx={{ height: '200px', position: 'relative' }}>
                    <input
                      accept=".pdf, .jpg, .jpeg, .png, .xlsx, .xls"
                      className="fileInput"
                      id={`file-upload-${docType.value}`}
                      type="file"
                      onChange={(e) => handleFileUpload(e, docType)}
                    />
                    <label
                      htmlFor={`file-upload-${docType.value}`}
                      style={{ width: '100%', height: '150px', display: 'block' }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          overflow: 'hidden'
                        }}
                      >
                        {!doc?.preview ? (
                          <>
                            <BiCloudUpload className="upladIcon" style={{ fontSize: '3rem' }} />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Click to upload {docType.label}
                            </Typography>
                          </>
                        ) : isPdf ? (
                          <Document file={doc.preview} onLoadSuccess={onDocumentLoadSuccess}>
                            <Pages pageNumber={pageNumber} width={150} />
                          </Document>
                        ) : doc?.extension === 'xlsx' || doc?.extension === 'xls' ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <img
                              src="https://cdn-icons-png.flaticon.com/512/732/732220.png"
                              alt="Excel"
                              style={{ width: '50px' }}
                            />
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {doc.file?.name}
                            </Typography>
                          </Box>
                        ) : (
                          <img
                            src={doc.preview}
                            alt={docType.label}
                            className="previewImage"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        )}
                      </Box>
                    </label>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', paddingTop: '1.5rem' }}>
        <Button
          onClick={handlePrev}
          startIcon={<HiOutlineArrowNarrowLeft />}
          className="mainBtn previousBtn"
        >
          Back
        </Button>
        <Button
          onClick={(e) => handleNextStep4(e)}
          endIcon={<HiOutlineArrowNarrowRight />}
          className="mainBtn nextBtn"
        >
          Next
        </Button>
      </Box>

      <Dialog
        open={open3}
        onClose={handleClose3}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{validationMsg3}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose3}>Retry</Button>
        </DialogActions>
      </Dialog>
      <Loader openLoader={openLoader} />
    </Box>
  );
}
